import { createSlice } from '@reduxjs/toolkit'
import { createApiAsyncThunk } from '@/services/apiService'
import { addFlashMessage } from '../flash/flashMessagesSlice'
import { completeProcessTask, startProcessTask, cancelProcessTask, archiveProcessContainer } from './processTasksSlice'

const MACRO_URL = 'macro_processes'
const TASKS_URL = 'process_tasks'

// ==================== ASYNC THUNKS ====================

export const fetchMacroProcesses = createApiAsyncThunk(
  'macroProcesses/fetchAll',
  async (params = {}, { api, rejectWithValue }) => {
    try {
      const response = await api
        .url(MACRO_URL)
        .query(params)
        .get()
      return response
    } catch (error) {
      return rejectWithValue(error.message || 'Erreur lors du chargement des macro processes')
    }
  }
)

export const fetchTasksForContainers = createApiAsyncThunk(
  'macroProcesses/fetchTasks',
  async (containerIds, { api, rejectWithValue }) => {
    try {
      const params = {}
      containerIds.forEach((id, index) => {
        params[`process_container_ids[${index}]`] = id
      })
      const response = await api
        .url(TASKS_URL)
        .query(params)
        .get()
      return { tasks: response.tasks || [], containerIds }
    } catch (error) {
      return rejectWithValue(error.message || 'Erreur lors du chargement des tâches')
    }
  }
)

export const archiveMacroProcess = createApiAsyncThunk(
  'macroProcesses/archive',
  async (macroProcessId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${MACRO_URL}/${macroProcessId}/archive`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Macro process archivé' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const deleteMacroProcess = createApiAsyncThunk(
  'macroProcesses/delete',
  async (macroProcessId, { api, dispatch, rejectWithValue }) => {
    try {
      await api
        .url(`${MACRO_URL}/${macroProcessId}`)
        .delete()
      dispatch(addFlashMessage({ type: 'success', text: 'Macro process supprimé' }))
      return { id: macroProcessId }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const validateProcessTask = createApiAsyncThunk(
  'macroProcesses/validateTask',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${TASKS_URL}/${taskId}/validate`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche validée' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const reopenProcessTask = createApiAsyncThunk(
  'macroProcesses/reopenTask',
  async (taskId, { api, dispatch, rejectWithValue }) => {
    try {
      const response = await api
        .url(`${TASKS_URL}/${taskId}/reopen`)
        .post()
      dispatch(addFlashMessage({ type: 'success', text: 'Tâche réouverte' }))
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// ==================== HELPERS ====================

const updateTaskInCache = (state, updatedTask) => {
  const containerId = updatedTask.process_container_id
  if (!containerId) return
  if (!state.tasks[containerId]) return
  const idx = state.tasks[containerId].findIndex(t => t.id === updatedTask.id)
  if (idx !== -1) {
    state.tasks[containerId][idx] = updatedTask
  }
}

const updateProcessContainer = (state, updatedProcessContainer) => {
  const macroProcessId = updatedProcessContainer.macro_process_id
  if (!macroProcessId) return
  const idx = state.items.findIndex(m => m.id === macroProcessId)
  if(idx !== -1) {
    const processContainerIdx = state.items[idx].process_containers.findIndex(c => c.id === updatedProcessContainer.id)
    if(processContainerIdx !== -1) {
      const processContainer = state.items[idx].process_containers[processContainerIdx]
      state.items[idx].process_containers[processContainerIdx] = {
        ...processContainer,
        ...updatedProcessContainer
      }
    }
  }
}

const updateMacroProcessInList = (state, updatedMacroProcess) => {
  const idx = state.items.findIndex(mp => mp.id === updatedMacroProcess.id)
  if (idx !== -1) {
    state.items[idx] = {
      ...state.items[idx],
      ...updatedMacroProcess
    }
  }
}

// ==================== INITIAL STATE ====================

const initialState = {
  items: [],
  tasks: {},
  // biens: [],
  loading: {
    fetchList: false,
    fetchTasks: false,
    fetchBiens: false,
    action: false
  },
  error: null,
  filters: {
    page: 1,
    bien_id: '',
    status: 'pending'
  },
  pagination: {
    totalCount: 0,
    totalPages: 0,
    currentPage: 1
  }
}

// ==================== SLICE ====================

const macroProcessesSlice = createSlice({
  name: 'macroProcesses',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    // Fetch macro processes
    builder.addCase(fetchMacroProcesses.pending, (state) => {
      state.loading.fetchList = true
      state.error = null
    })
    builder.addCase(fetchMacroProcesses.fulfilled, (state, action) => {
      state.loading.fetchList = false
      state.items = action.payload.macro_processes || []
      state.pagination = {
        totalCount: action.payload.pagination?.total_count || 0,
        totalPages: action.payload.pagination?.total_pages || 0,
        currentPage: action.payload.pagination?.current_page || 1
      }
    })
    builder.addCase(fetchMacroProcesses.rejected, (state, action) => {
      state.loading.fetchList = false
      state.error = action.payload
    })

    // Fetch tasks for containers
    builder.addCase(fetchTasksForContainers.pending, (state) => {
      state.loading.fetchTasks = true
    })
    builder.addCase(fetchTasksForContainers.fulfilled, (state, action) => {
      state.loading.fetchTasks = false
      const { tasks } = action.payload
      // Regrouper les tasks par process_container_id
      const grouped = {}
      tasks.forEach(task => {
        const cId = task.process_container_id
        if (!cId) return
        if (!grouped[cId]) grouped[cId] = []
        grouped[cId].push(task)
      })
      state.tasks = { ...state.tasks, ...grouped }
    })
    builder.addCase(fetchTasksForContainers.rejected, (state, action) => {
      state.loading.fetchTasks = false
      state.error = action.payload
    })

    // Archive macro process
    builder.addCase(archiveMacroProcess.pending, (state) => {
      state.loading.action = true
    })
    builder.addCase(archiveMacroProcess.fulfilled, (state, action) => {
      state.loading.action = false
      updateMacroProcessInList(state, action.payload)
      // Supprimer les tâches cachées de ce macro process
      // Commenté le 20/04/2026 car juger non nécessaire de supprimer les tâches
      // const containerId = action.payload.process_containers?.map(c => c.id) || []
      // containerId.forEach(id => {
      //   delete state.tasks[id]
      // })
    })
    builder.addCase(archiveMacroProcess.rejected, (state, action) => {
      state.loading.action = false
      state.error = action.payload
    })

    // Delete macro process
    builder.addCase(deleteMacroProcess.fulfilled, (state, action) => {
      state.items = state.items.filter(mp => mp.id !== action.payload.id)
    })

    // Validate task
    builder.addCase(validateProcessTask.pending, (state) => {
      state.loading.action = true
    })
    builder.addCase(validateProcessTask.fulfilled, (state, action) => {
      state.loading.action = false
      updateTaskInCache(state, action.payload)
      updateProcessContainer(state, action.payload.process_container)
      updateMacroProcessInList(state, action.payload.macro_process)
    })
    builder.addCase(validateProcessTask.rejected, (state, action) => {
      state.loading.action = false
      state.error = action.payload
    })

    // Reopen task
    builder.addCase(reopenProcessTask.pending, (state) => {
      state.loading.action = true
    })
    builder.addCase(reopenProcessTask.fulfilled, (state, action) => {
      state.loading.action = false
      updateTaskInCache(state, action.payload)
    })
    builder.addCase(reopenProcessTask.rejected, (state, action) => {
      state.loading.action = false
      state.error = action.payload
    })

    // Écouter les actions processTasksSlice pour mettre à jour le cache
    builder.addCase(completeProcessTask.fulfilled, (state, action) => {
      updateTaskInCache(state, action.payload)
    })
    builder.addCase(startProcessTask.fulfilled, (state, action) => {
      updateTaskInCache(state, action.payload)
    })
    builder.addCase(cancelProcessTask.fulfilled, (state, action) => {
      updateTaskInCache(state, action.payload)
    })
    builder.addCase(archiveProcessContainer.fulfilled, (state, action) => {
      updateProcessContainer(state, action.payload)
      updateMacroProcessInList(state, action.payload.macro_process)
    })
  }
})

export const { setFilters, resetFilters } = macroProcessesSlice.actions

export default macroProcessesSlice.reducer
